import {
  ON_CHAIN_TOKEN_ADDRESSES,
  type OnChainTokenSymbol,
} from './blockchain';

const GECKO_TERMINAL_BASE_URL = 'https://api.geckoterminal.com/api/v2';
const WORLD_CHAIN_NETWORK = 'world-chain';
const PRICE_CACHE_TTL_MS = 60_000;
const PRICE_REQUEST_TIMEOUT_MS = 8_000;
const BBT_REFERENCE_POOL_ADDRESS = '0xbe4b23e913e849e0e91e499db9e0b8b34abad952';

export interface TokenPrice {
  symbol: OnChainTokenSymbol;
  address: string;
  usd: number | null;
  fetchedAt: number;
}

export interface TokenPricesResult {
  prices: Partial<Record<OnChainTokenSymbol, TokenPrice>>;
  fetchedAt: number;
}

interface GeckoTerminalResponse {
  data?: {
    attributes?: {
      token_prices?: Record<string, string | null>;
    };
  };
}

interface GeckoPoolResource {
  attributes?: {
    base_token_price_usd?: string | null;
    quote_token_price_usd?: string | null;
    name?: string | null;
  };
  relationships?: {
    base_token?: { data?: { id?: string } };
    quote_token?: { data?: { id?: string } };
  };
}

interface GeckoPoolResponse {
  data?: GeckoPoolResource | GeckoPoolResource[];
}

let cachedPrices: { expiresAt: number; result: TokenPricesResult } | null = null;
let inFlightRequest: Promise<TokenPricesResult> | null = null;

function parseUsdPrice(value: string | null | undefined): number | null {
  if (typeof value !== 'string' || value.trim() === '') return null;

  const price = Number(value);
  return Number.isFinite(price) && price > 0 ? price : null;
}

async function fetchJson<T>(path: string): Promise<T> {
  const response = await fetch(`${GECKO_TERMINAL_BASE_URL}${path}`, {
    headers: {
      Accept: 'application/json;version=20230203',
    },
    signal: AbortSignal.timeout(PRICE_REQUEST_TIMEOUT_MS),
  });

  if (!response.ok) {
    throw new Error(`價格服務回應失敗（${response.status}）。`);
  }

  return response.json() as Promise<T>;
}

function getPoolResources(response: GeckoPoolResponse): GeckoPoolResource[] {
  if (!response.data) return [];
  return Array.isArray(response.data) ? response.data : [response.data];
}

function getRelationshipAddress(id: string | undefined): string | null {
  if (!id) return null;
  const address = id.split('_').pop();
  return address?.toLowerCase() ?? null;
}

function getPoolTokenPriceUsd(
  pool: GeckoPoolResource | undefined,
  tokenAddress: string,
): number | null {
  const attributes = pool?.attributes;
  if (!attributes) return null;

  const normalizedTokenAddress = tokenAddress.toLowerCase();
  const baseAddress = getRelationshipAddress(pool.relationships?.base_token?.data?.id);
  const quoteAddress = getRelationshipAddress(pool.relationships?.quote_token?.data?.id);

  if (baseAddress === normalizedTokenAddress) {
    return parseUsdPrice(attributes.base_token_price_usd);
  }

  if (quoteAddress === normalizedTokenAddress) {
    return parseUsdPrice(attributes.quote_token_price_usd);
  }

  return null;
}

async function fetchTokenPrices(): Promise<TokenPricesResult> {
  const addresses = Object.values(ON_CHAIN_TOKEN_ADDRESSES).join(',');
  const [simpleResult, bbtPoolResult, ggmomomoPoolsResult] = await Promise.allSettled([
    fetchJson<GeckoTerminalResponse>(
      `/simple/networks/${WORLD_CHAIN_NETWORK}/token_price/${addresses}`,
    ),
    fetchJson<GeckoPoolResponse>(
      `/networks/${WORLD_CHAIN_NETWORK}/pools/${BBT_REFERENCE_POOL_ADDRESS}`,
    ),
    fetchJson<GeckoPoolResponse>(
      `/networks/${WORLD_CHAIN_NETWORK}/tokens/${ON_CHAIN_TOKEN_ADDRESSES.GGMOMOMO}/pools`,
    ),
  ]);

  if ([simpleResult, bbtPoolResult, ggmomomoPoolsResult]
    .every((result) => result.status === 'rejected')) {
    throw new Error('價格服務暫時無法連線。');
  }

  const tokenPrices = simpleResult.status === 'fulfilled'
    ? simpleResult.value.data?.attributes?.token_prices ?? {}
    : {};
  const bbtPool = bbtPoolResult.status === 'fulfilled'
    ? getPoolResources(bbtPoolResult.value)[0]
    : undefined;
  const ggmomomoPool = ggmomomoPoolsResult.status === 'fulfilled'
    ? getPoolResources(ggmomomoPoolsResult.value)[0]
    : undefined;
  const fetchedAt = Date.now();
  const prices: Partial<Record<OnChainTokenSymbol, TokenPrice>> = {};

  for (const [symbol, address] of Object.entries(ON_CHAIN_TOKEN_ADDRESSES)) {
    const simplePrice = parseUsdPrice(tokenPrices[address.toLowerCase()]);
    const poolPrice = symbol === 'BBT'
      ? getPoolTokenPriceUsd(bbtPool, address)
      : symbol === 'GGMOMOMO'
        ? getPoolTokenPriceUsd(ggmomomoPool, address)
        : null;
    prices[symbol as OnChainTokenSymbol] = {
      symbol: symbol as OnChainTokenSymbol,
      address,
      usd: poolPrice ?? simplePrice,
      fetchedAt,
    };
  }

  return { prices, fetchedAt };
}

export async function getOnChainTokenPrices(
  forceRefresh = false,
): Promise<TokenPricesResult> {
  const now = Date.now();
  if (!forceRefresh && cachedPrices && cachedPrices.expiresAt > now) {
    return cachedPrices.result;
  }

  if (inFlightRequest) return inFlightRequest;

  inFlightRequest = fetchTokenPrices()
    .then((result) => {
      cachedPrices = {
        expiresAt: result.fetchedAt + PRICE_CACHE_TTL_MS,
        result,
      };
      return result;
    })
    .finally(() => {
      inFlightRequest = null;
    });

  return inFlightRequest;
}

export function formatUsdPrice(price: number | null | undefined): string {
  if (price == null || !Number.isFinite(price)) return '—';

  if (price >= 1) {
    return `USD $${price.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }

  if (price >= 0.01) {
    return `USD $${price.toLocaleString('en-US', {
      minimumFractionDigits: 4,
      maximumFractionDigits: 4,
    })}`;
  }

  return `USD $${price.toLocaleString('en-US', {
    minimumFractionDigits: 6,
    maximumFractionDigits: 8,
  })}`;
}

export function formatUsdValue(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—';

  return `USD $${value.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}
